// src/components/membership/MembershipPlans-SubscriptionStatus.js
// =============================================================================
// Centralized membership subscription status + plan feature definitions.
// - Legacy Stripe status is checked first from the authoritative account record.
// - If legacy Stripe is present, immediately resolve as BlackGold (Legacy).
// - Otherwise, resolve from subscription rows from a_payments/get-subscriptions-new.
// - If neither legacy Stripe nor qualifying subscription rows exist, resolve as WildCatters.
// - Trial detection: TrialEndDate takes precedence when present; otherwise, trial row without a Stripe subscription id.
// - Plan entitlement: explicit paid DB plan ids are authoritative.
// - Fetching the authoritative subscription rows now also lives in this module.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../services/AuthService.js";


// --------------------------------------------------------------------------------
// Blocker for Viewing
export const MEMBERSHIP_ENV = Object.freeze({
    local: "local",
    dev: "dev",
    qa: "qa",
    prod: "prod",
});

export function getMembershipEnvironment(baseUrl = "") {
    const normalizedBaseUrl = String(baseUrl || "").toLowerCase();

    if (normalizedBaseUrl.includes("local")) return MEMBERSHIP_ENV.local;
    if (normalizedBaseUrl.includes("dev")) return MEMBERSHIP_ENV.dev;
    if (normalizedBaseUrl.includes("qa")) return MEMBERSHIP_ENV.qa;
    if (normalizedBaseUrl.includes("prod")) return MEMBERSHIP_ENV.prod;

    return "";
}

/** @deprecated Environment gating removed — always enabled. */
export function isMembershipEnvironmentEnabled() {
    return true;
}
// --------------------------------------------------------------------------------

export const SUBSCRIPTION_TYPE_ID = Object.freeze({
    WildCatters: 1,
    BlackGold: 2, // legacy-only; not used for paid DB entitlements
    Trial: 3,
    Revenue_BlackGold: 4,
    JIB_BlackGold: 5,
    Oil_Baron: 6,
    Big_Rig: 7,
});

export const PLAN_ID = Object.freeze({
    wildcatter: "wildcatter",
    blackgoldRevenue: "blackgold-revenue",
    blackgoldJib: "blackgold-jib",
    oilBaron: "oil-baron",
    bigRig: "big-rig",
});

export const PLAN_RANK = Object.freeze({
    [PLAN_ID.wildcatter]: 0,
    [PLAN_ID.blackgoldRevenue]: 1,
    [PLAN_ID.blackgoldJib]: 1,
    [PLAN_ID.oilBaron]: 2,
    [PLAN_ID.bigRig]: 3,
});

export const STRIPE_PRICE_ID = Object.freeze({
    Revenue_BlackGold: process.env.REACT_APP_STRIPE_PRICE_REVENUE_BLACKGOLD || "",
    JIB_BlackGold: process.env.REACT_APP_STRIPE_PRICE_JIB_BLACKGOLD || "",
    Oil_Baron: process.env.REACT_APP_STRIPE_PRICE_OIL_BARON || "",
});

export const FEATURE = Object.freeze({
    revenuePdf: "Revenue Statements (PDF Format)",
    jibPdf: "JIB Statements (PDF Format)",
    form1099Pdf: "1099 Statements (PDF Format)",
    revenueCdex: "Revenue – CDEX Format",
    revenueXlsx: "Revenue – XLSX Format",
    jibXlsx: "JIB – XLSX Format",
    prioritySupport: "Priority Online Support",
    directContact: "Direct contact for pricing",
});

export const FEATURE_ORDER = Object.freeze([
    FEATURE.revenuePdf,
    FEATURE.jibPdf,
    FEATURE.form1099Pdf,
    FEATURE.revenueCdex,
    FEATURE.revenueXlsx,
    FEATURE.jibXlsx,
    FEATURE.prioritySupport,
    FEATURE.directContact,
]);

export const PLAN_TITLE_BY_ID = Object.freeze({
    [PLAN_ID.wildcatter]: "WILDCATTER",
    [PLAN_ID.blackgoldRevenue]: "BLACKGOLD - REVENUE",
    [PLAN_ID.blackgoldJib]: "BLACKGOLD - JIB",
    [PLAN_ID.oilBaron]: "OIL BARON",
    [PLAN_ID.bigRig]: "BIG RIG",
});

export const PLAN_INCLUDES_BY_ID = Object.freeze({
    [PLAN_ID.wildcatter]: Object.freeze([]),
    [PLAN_ID.blackgoldRevenue]: Object.freeze([PLAN_ID.wildcatter]),
    [PLAN_ID.blackgoldJib]: Object.freeze([PLAN_ID.wildcatter]),
    [PLAN_ID.oilBaron]: Object.freeze([PLAN_ID.wildcatter, PLAN_ID.blackgoldRevenue, PLAN_ID.blackgoldJib]),
    [PLAN_ID.bigRig]: Object.freeze([PLAN_ID.oilBaron]),
});

export const PLAN_BASE_FEATURES_BY_ID = Object.freeze({
    [PLAN_ID.wildcatter]: Object.freeze([FEATURE.revenuePdf, FEATURE.jibPdf, FEATURE.form1099Pdf]),
    [PLAN_ID.blackgoldRevenue]: Object.freeze([FEATURE.revenueCdex, FEATURE.revenueXlsx, FEATURE.prioritySupport]),
    [PLAN_ID.blackgoldJib]: Object.freeze([FEATURE.jibXlsx, FEATURE.prioritySupport]),
    [PLAN_ID.oilBaron]: Object.freeze([]),
    [PLAN_ID.bigRig]: Object.freeze([FEATURE.directContact]),
});

export const PLAN_TILE_FEATURES_BY_ID = Object.freeze({
    [PLAN_ID.wildcatter]: Object.freeze([]),
    [PLAN_ID.blackgoldRevenue]: Object.freeze([FEATURE.revenueXlsx]),
    [PLAN_ID.blackgoldJib]: Object.freeze([FEATURE.jibXlsx]),
    [PLAN_ID.oilBaron]: Object.freeze(["Revenue + JIB – XLSX Format"]),
    [PLAN_ID.bigRig]: Object.freeze([FEATURE.directContact]),
});

export function getPlanTitle(planId) {
    const id = (planId || "").toString();
    return PLAN_TITLE_BY_ID[id] || (id ? id.toUpperCase() : "");
}

export function getPlanIncludes(planId) {
    const id = (planId || "").toString();
    return Array.isArray(PLAN_INCLUDES_BY_ID[id]) ? Array.from(PLAN_INCLUDES_BY_ID[id]) : [];
}

export function getPlanBaseFeatures(planId) {
    const id = (planId || "").toString();
    return Array.isArray(PLAN_BASE_FEATURES_BY_ID[id]) ? Array.from(PLAN_BASE_FEATURES_BY_ID[id]) : [];
}

export function getPlanTileFeatures(planId) {
    const id = (planId || "").toString();
    return Array.isArray(PLAN_TILE_FEATURES_BY_ID[id]) ? Array.from(PLAN_TILE_FEATURES_BY_ID[id]) : [];
}

export function normalizeFeatureKey(featureLabel) {
    return (featureLabel || "").toString().trim().toLowerCase();
}

export function normalizeSubscriptionRow(raw) {
    const subscriptionTypeId = Number(
        raw?.subscriptionTypeId ??
        raw?.SubscriptionTypeId ??
        raw?.subscription_type_id ??
        raw?.Subscription_Type_Id ??
        raw?.subscriptionType ??
        raw?.SubscriptionType ??
        0
    );

    const stripeIdCandidates = [
        raw?.stripeSubscriptionId,
        raw?.StripeSubscriptionId,
        raw?.stripeSubscriptionID,
        raw?.StripeSubscriptionID,
        raw?.stripe_subscription_id,
        raw?.stripeSubId,
        raw?.StripeSubId,
        raw?.subscriptionId,
        raw?.SubscriptionId,
        raw?.subscriptionID,
        raw?.SubscriptionID,
    ]
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v).trim())
        .filter(Boolean);

    const stripeSubscriptionId = stripeIdCandidates.find((v) => /^sub_/.test(v)) || "";

    const trialEndDate =
        raw?.trialEndDate ?? raw?.TrialEndDate ?? raw?.trialEndDateString ?? raw?.TrialEndDateString ?? raw?.trial_end_date ?? null;

    const label = (
        raw?.subscriptionName ??
        raw?.SubscriptionName ??
        raw?.subscriptionTypeName ??
        raw?.SubscriptionTypeName ??
        raw?.name ??
        raw?.Name ??
        ""
    ).toString();

    const subscriptionRowId = Number(raw?.subscriptionRowId ?? raw?.SubscriptionRowId ?? raw?.id ?? raw?.Id ?? raw?.subscriptionIdInt ?? 0);

    return { subscriptionTypeId, stripeSubscriptionId, trialEndDate, label, subscriptionRowId };
}

function coerceDateToMillis(value) {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) {
        const t = value.getTime();
        return Number.isFinite(t) ? t : null;
    }
    if (typeof value === "number") {
        const ms = value > 10_000_000_000 ? value : value * 1000;
        return Number.isFinite(ms) ? ms : null;
    }
    const s0 = String(value || "").trim();
    if (!s0) return null;

    // ASP.NET often serializes UTC DateTime values without an explicit timezone when Kind is lost.
    // When an ISO string has no timezone suffix, interpret it as UTC to avoid local-time skew.
    const hasTimezoneSuffix = /([zZ]|[+\-]\d{2}:\d{2})$/.test(s0);
    const looksIsoLocal = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s0);
    const s = !hasTimezoneSuffix && looksIsoLocal ? `${s0}Z` : s0;

    const t = Date.parse(s);
    return Number.isFinite(t) ? t : null;
}

export function isBlackGoldPeerLocked(planId, effectiveCurrentPlanId) {
    if (effectiveCurrentPlanId === PLAN_ID.blackgoldRevenue) return planId === PLAN_ID.blackgoldJib;
    if (effectiveCurrentPlanId === PLAN_ID.blackgoldJib) return planId === PLAN_ID.blackgoldRevenue;
    return false;
}

export function getCancelableSubscriptionLabel(subscriptionTypeId) {
    if (subscriptionTypeId === SUBSCRIPTION_TYPE_ID.Revenue_BlackGold) return "BlackGold Revenue";
    if (subscriptionTypeId === SUBSCRIPTION_TYPE_ID.JIB_BlackGold) return "BlackGold JIB";
    if (subscriptionTypeId === SUBSCRIPTION_TYPE_ID.Oil_Baron) return "Oil Baron";
    if (subscriptionTypeId === SUBSCRIPTION_TYPE_ID.Big_Rig) return "Big Rig";
    if (subscriptionTypeId === SUBSCRIPTION_TYPE_ID.BlackGold) return "BlackGold (Legacy)";
    return `Subscription ${subscriptionTypeId}`;
}

function resolveCanonicalPlanId(rawPlanId) {
    const id = (rawPlanId || "").toString().trim().toLowerCase().replace(/[|]/g, "").replace(/_/g, "-").replace(/\s+/g, "-").replace(/--+/g, "-");
    if (!id) return "";
    if (id.includes("oil") && id.includes("baron")) return PLAN_ID.oilBaron;
    if (id.includes("blackgold") && id.includes("revenue")) return PLAN_ID.blackgoldRevenue;
    if (id.includes("blackgold") && id.includes("jib")) return PLAN_ID.blackgoldJib;
    if (id.includes("big") && id.includes("rig")) return PLAN_ID.bigRig;
    if (id.includes("wildcatter")) return PLAN_ID.wildcatter;
    return id;
}

export function computeEffectivePlanIdFromSubscriptionRows(rawRows) {
    const rows = (rawRows || []).map(normalizeSubscriptionRow);

    const isPaidRow = (r) => r.subscriptionTypeId !== SUBSCRIPTION_TYPE_ID.Trial;
    const paidTypeIds = new Set(rows.filter(isPaidRow).map((r) => r.subscriptionTypeId));

    const hasBigRigPaid = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.Big_Rig);
    if (hasBigRigPaid) return PLAN_ID.bigRig;

    // SubscriptionTypeId 2 (BlackGold Legacy) is treated as BlackGold-Revenue (type 4)
    const hasRevenue = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.Revenue_BlackGold) || paidTypeIds.has(SUBSCRIPTION_TYPE_ID.BlackGold);
    const hasJib = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.JIB_BlackGold);

    const hasOilBaronPaid = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.Oil_Baron) || (hasRevenue && hasJib);
    if (hasOilBaronPaid) return PLAN_ID.oilBaron;

    if (hasRevenue) return PLAN_ID.blackgoldRevenue;
    if (hasJib) return PLAN_ID.blackgoldJib;

    const trialRows = rows.filter((r) => r.subscriptionTypeId === SUBSCRIPTION_TYPE_ID.Trial);
    const hasTrialRow = trialRows.length > 0;
    const trialEndMsCandidates = trialRows.map((r) => coerceDateToMillis(r.trialEndDate)).filter((v) => Number.isFinite(v));
    const trialEndMs = trialEndMsCandidates.length ? Math.max(...trialEndMsCandidates) : null;

    const hasTrialEndDate = trialEndMs !== null;

    // Trial is active only when a Trial row (subscriptionTypeId=3) exists
    // with a TrialEndDate that is present and in the future.
    // NULL TrialEndDate on a Trial row is treated as expired.
    const isTrialActive = hasTrialRow && hasTrialEndDate && Date.now() <= trialEndMs;
    if (isTrialActive) return PLAN_ID.oilBaron;

    return PLAN_ID.wildcatter;
}

export function getAllFeaturesForPlanId(planId) {
    const visited = new Set();
    const out = [];

    const walk = (id) => {
        if (!id || visited.has(id)) return;
        visited.add(id);

        getPlanIncludes(id).forEach(walk);

        getPlanBaseFeatures(id).forEach((feat) => {
            if (!out.includes(feat)) out.push(feat);
        });
    };

    walk((planId || "").toString());

    const rank = new Map(FEATURE_ORDER.map((f, i) => [f, i]));
    out.sort((a, b) => (rank.get(a) ?? 999) - (rank.get(b) ?? 999) || a.localeCompare(b));

    return out;
}

export function hasFeature(planId, featureLabel) {
    const features = getAllFeaturesForPlanId(planId);
    return features.includes(featureLabel);
}

export function getFeaturesToUnlock(currentPlanId, targetPlanId) {
    const current = new Set(getAllFeaturesForPlanId(currentPlanId));
    return getAllFeaturesForPlanId(targetPlanId).filter((f) => !current.has(f));
}

export function computeMembershipStatusFromSubscriptionRows(rawRows) {
    const rows = (rawRows || []).map(normalizeSubscriptionRow);

    const hasDbSubscriptions = rows.length > 0;
    const isPaidRow = (r) => r.subscriptionTypeId !== SUBSCRIPTION_TYPE_ID.Trial;

    const paidRows = rows.filter(isPaidRow);
    const resolvedRows = paidRows.length > 0 ? paidRows : rows;
    const paidTypeIds = new Set(paidRows.map((r) => r.subscriptionTypeId));

    const trialRows = rows.filter((r) => r.subscriptionTypeId === SUBSCRIPTION_TYPE_ID.Trial);
    const hasTrialRow = trialRows.length > 0;
    const trialEndMsCandidates = trialRows.map((r) => coerceDateToMillis(r.trialEndDate)).filter((v) => Number.isFinite(v));
    const trialEndMs = trialEndMsCandidates.length ? Math.max(...trialEndMsCandidates) : null;

    const hasTrialEndDate = trialEndMs !== null;

    // Trial is active only when a Trial row (subscriptionTypeId=3) exists
    // with a TrialEndDate that is present and in the future.
    // NULL TrialEndDate on a Trial row is treated as expired.
    const isTrialActive = paidRows.length === 0 && hasTrialRow && hasTrialEndDate && Date.now() <= trialEndMs;
    const isTrialExpired = paidRows.length === 0 && hasTrialRow && (!hasTrialEndDate || Date.now() > trialEndMs);
    const isTrial = isTrialActive;

    // SubscriptionTypeId 2 (BlackGold Legacy) is treated as BlackGold-Revenue (type 4)
    const hasRevenue = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.Revenue_BlackGold) || paidTypeIds.has(SUBSCRIPTION_TYPE_ID.BlackGold);
    const hasJib = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.JIB_BlackGold);

    const hasOilBaronPaid = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.Oil_Baron) || (hasRevenue && hasJib);
    const hasBigRigPaid = paidTypeIds.has(SUBSCRIPTION_TYPE_ID.Big_Rig);

    const effectivePlanId = (() => {
        if (hasBigRigPaid) return PLAN_ID.bigRig;
        if (hasOilBaronPaid) return PLAN_ID.oilBaron;
        if (hasRevenue) return PLAN_ID.blackgoldRevenue;
        if (hasJib) return PLAN_ID.blackgoldJib;
        if (isTrialActive) return PLAN_ID.oilBaron;
        return PLAN_ID.wildcatter;
    })();

    const kind = isTrialActive ? "trial" : effectivePlanId !== PLAN_ID.wildcatter ? "paid" : "free";
    const memberTypeLabel = kind === "paid" ? "Paid Member" : kind === "trial" ? "Trial" : "Free Member";

    return {
        source: "db",
        hasDbSubscriptions,
        rows: resolvedRows,
        paidTypeIds,
        isTrial,
        isTrialActive,
        isTrialExpired,
        trialEndMs,
        kind,
        memberTypeLabel,
        effectivePlanId,
        hasRevenue,
        hasJib,
        hasOilBaronPaid,
        hasBigRigPaid,
    };
}

// Legacy membership detection is intentionally strict/simple:
// if Account.StripeSubscriptionId exists => legacy paid (BlackGold)
// This helper exists because the legacy API payloads have historically used multiple casing variants.
export function getLegacyStripeSubscriptionId(accountProfile) {
    const raw = accountProfile || {};

    // Some callers historically pass a nested object (e.g., { account: {...} })
    // so search both the root and a few common nested keys.
    const roots = [
        raw,
        raw.account,
        raw.Account,
        raw.accountProfile,
        raw.AccountProfile,
    ].filter(Boolean);

    const candidates = roots
        .flatMap((r) => [
            r?.stripeSubscriptionId,
            r?.StripeSubscriptionId,
            r?.stripeSubscriptionID,
            r?.StripeSubscriptionID,
            r?.stripe_subscription_id,
            r?.Stripe_Subscription_Id,
            r?.stripeSubId,
            r?.StripeSubId,
            r?.stripeSubID,
            r?.StripeSubID,
            r?.subscriptionId,
            r?.SubscriptionId,
            r?.subscriptionID,
            r?.SubscriptionID,
        ])
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v).trim())
        .filter(Boolean);

    // Prefer actual Stripe subscription ids when present.
    return candidates.find((v) => /^sub_/.test(v)) || candidates[0] || "";
}

export function computeLegacyMembershipStatus(accountProfile, { legacySubscriptionStatusId = 0, legacyPlanId = "" } = {}) {
    // Legacy classification rule (used by the legacy Membership page and as a fallback):
    // - If Account.StripeSubscriptionId is present => BlackGold (Legacy)
    // - If Account.StripeSubscriptionId is missing => WildCatters (New)
    // Notes:
    // - legacySubscriptionStatusId/legacyPlanId/subscriptionName are intentionally ignored to avoid stale legacy values after cancellations.
    const legacyStripeSubscriptionId = getLegacyStripeSubscriptionId(accountProfile);
    const isBlackGoldLegacy = Boolean(legacyStripeSubscriptionId);

    const effectivePlanId = isBlackGoldLegacy ? PLAN_ID.blackgoldRevenue : PLAN_ID.wildcatter;
    const kind = isBlackGoldLegacy ? "paid" : "free";
    const memberTypeLabel = kind === "paid" ? "Paid Member" : "Free Member";
    const planLabel = isBlackGoldLegacy ? "BlackGold (Legacy)" : "WildCatters (New)";

    return {
        source: "legacy",
        hasDbSubscriptions: false,
        rows: [],
        paidTypeIds: new Set(),
        isTrial: false,
        isTrialActive: false,
        isTrialExpired: false,
        trialEndMs: null,
        kind,
        memberTypeLabel,
        effectivePlanId,
        planLabel,
        isBlackGoldLegacy,
        legacyStripeSubscriptionId,
        hasRevenue: effectivePlanId === PLAN_ID.blackgoldRevenue || effectivePlanId === PLAN_ID.oilBaron || effectivePlanId === PLAN_ID.bigRig,
        hasJib: effectivePlanId === PLAN_ID.blackgoldJib || effectivePlanId === PLAN_ID.oilBaron || effectivePlanId === PLAN_ID.bigRig,
        hasOilBaronPaid: effectivePlanId === PLAN_ID.oilBaron || effectivePlanId === PLAN_ID.bigRig,
        hasBigRigPaid: effectivePlanId === PLAN_ID.bigRig,
    };
}

// Compatibility helper for older callers (e.g., Membership.js) that may not have Accounts.StripeSubscriptionId in the hydrated account DTO.
// Prefer the strict legacy rule (StripeSubscriptionId present) and only fall back to legacySubscriptionStatusId / legacyPlanId / subscriptionName when necessary.
export function computeLegacyMembershipStatusWithFallback(accountProfile, { legacySubscriptionStatusId = 0, legacyPlanId = "" } = {}) {
    const strict = computeLegacyMembershipStatus(accountProfile, { legacySubscriptionStatusId, legacyPlanId });
    if (strict?.isBlackGoldLegacy) return strict;

    const legacyName = (accountProfile?.subscriptionName ?? accountProfile?.SubscriptionName ?? "").toString().trim();
    const statusIdFromProfile = Number(accountProfile?.subscriptionStatusId ?? accountProfile?.SubscriptionStatusId ?? 0);
    const statusId = Number(legacySubscriptionStatusId || statusIdFromProfile || 0);

    const planIdFromProfile = (accountProfile?.planId ?? accountProfile?.PlanId ?? accountProfile?.membershipPlanId ?? accountProfile?.MembershipPlanId ?? "").toString().trim();
    const planId = (legacyPlanId || planIdFromProfile || "").toString().trim().toLowerCase();

    const isLegacyTrial = legacyName === "Trial" || statusId === SUBSCRIPTION_TYPE_ID.Trial || planId.includes("trial");
    const isLegacyBlackGold = legacyName === "BlackGold" || planId.includes("blackgold");

    if (!isLegacyTrial && !isLegacyBlackGold) return strict;

    const kind = isLegacyTrial ? "trial" : "paid";
    const memberTypeLabel = kind === "paid" ? "Paid Member" : kind === "trial" ? "Trial" : "Free Member";
    const effectivePlanId = isLegacyBlackGold ? PLAN_ID.blackgoldRevenue : PLAN_ID.wildcatter;
    const planLabel = isLegacyTrial ? "BlackGold Trial (Legacy)" : "BlackGold (Legacy)";

    return {
        source: "legacy",
        hasDbSubscriptions: false,
        rows: [],
        paidTypeIds: new Set(),
        isTrial: isLegacyTrial,
        isTrialActive: isLegacyTrial,
        isTrialExpired: false,
        trialEndMs: null,
        kind,
        memberTypeLabel,
        effectivePlanId,
        planLabel,
        isBlackGoldLegacy: isLegacyBlackGold,
        legacyStripeSubscriptionId: "",
        hasRevenue: effectivePlanId === PLAN_ID.blackgoldRevenue || effectivePlanId === PLAN_ID.oilBaron || effectivePlanId === PLAN_ID.bigRig,
        hasJib: effectivePlanId === PLAN_ID.blackgoldJib || effectivePlanId === PLAN_ID.oilBaron || effectivePlanId === PLAN_ID.bigRig,
        hasOilBaronPaid: effectivePlanId === PLAN_ID.oilBaron || effectivePlanId === PLAN_ID.bigRig,
        hasBigRigPaid: effectivePlanId === PLAN_ID.bigRig,
    };
}

export function computeWildcatterStatus() {
    return {
        source: "resolved",
        hasDbSubscriptions: false,
        rows: [],
        paidTypeIds: new Set(),
        isTrial: false,
        isTrialActive: false,
        isTrialExpired: false,
        trialEndMs: null,
        kind: "free",
        memberTypeLabel: "Free Member",
        effectivePlanId: PLAN_ID.wildcatter,
        planLabel: "WildCatters (New)",
        isBlackGoldLegacy: false,
        legacyStripeSubscriptionId: "",
        hasRevenue: false,
        hasJib: false,
        hasOilBaronPaid: false,
        hasBigRigPaid: false,
    };
}

export function computeSubscriptionStatus({ subscriptionRows = null, accountProfile = null, legacySubscriptionStatusId = 0, legacyPlanId = "", preferLegacyStripeOnly = false, subscriptionRowsAuthoritative = false } = {}) {
    const legacyStatus = computeLegacyMembershipStatus(accountProfile, { legacySubscriptionStatusId, legacyPlanId });
    if (legacyStatus?.isBlackGoldLegacy) return legacyStatus;

    if (!preferLegacyStripeOnly && Array.isArray(subscriptionRows)) {
        if (subscriptionRowsAuthoritative || subscriptionRows.length > 0) {
            return computeMembershipStatusFromSubscriptionRows(subscriptionRows);
        }
    }

    return computeWildcatterStatus();
}

export function getOilBaronUpsellCheckoutItems(currentPlanId) {
    const id = (currentPlanId || "").toString();

    if (id === PLAN_ID.wildcatter) return { [STRIPE_PRICE_ID.Oil_Baron]: "1" };
    if (id === PLAN_ID.blackgoldRevenue) return { [STRIPE_PRICE_ID.JIB_BlackGold]: "1" };
    if (id === PLAN_ID.blackgoldJib) return { [STRIPE_PRICE_ID.Revenue_BlackGold]: "1" };
    return { [STRIPE_PRICE_ID.Oil_Baron]: "1" };
}

function resolveNumericAccountId(...values) {
    for (const value of values) {
        const numericValue = Number(
            value?.accountId ??
            value?.AccountId ??
            value?.id ??
            value?.Id ??
            value ??
            0
        );
        if (numericValue > 0) return numericValue;
    }
    return 0;
}

async function readResponseBody(response) {
    const text = await response.text();
    if (!text) return { json: null, text: "" };
    try {
        return { json: JSON.parse(text), text: "" };
    } catch {
        return { json: null, text };
    }
}

async function fetchAuthoritativeAccountRecord({ accountId, getAccessTokenSilently }) {
    if (!accountId || !getAccessTokenSilently) return null;

    const candidateUrls = [
        `accounts/${accountId}`,
        `accounts/get-account?accountId=${accountId}`,
        `accounts/getaccount?accountId=${accountId}`,
    ];

    for (const apiUrl of candidateUrls) {
        try {
            const response = await apiGet(apiUrl, getAccessTokenSilently);
            if (!response?.ok) continue;

            const body = await readResponseBody(response);
            const payload = body.json;
            if (Array.isArray(payload)) {
                const firstRecord = payload.find(Boolean);
                if (firstRecord && typeof firstRecord === "object") return firstRecord;
                continue;
            }

            if (payload && typeof payload === "object") {
                if (payload.account && typeof payload.account === "object") return payload.account;
                if (payload.Account && typeof payload.Account === "object") return payload.Account;
                return payload;
            }
        } catch {
            // Ignore and try the next account endpoint.
        }
    }

    return null;
}

export async function fetchMembershipSubscriptionState({
    accountId = 0,
    currentAccount = null,
    accountProfile = null,
    getAccessTokenSilently = null,
    includeCancelable = false,
    legacySubscriptionStatusId = 0,
    legacyPlanId = "",
    preferLegacyStripeOnly = false,
    baseUrl = "",
} = {}) {

    const resolvedAccountId = resolveNumericAccountId(accountId, currentAccount, accountProfile);
    const mergedAccountProfile = {
        ...(currentAccount ?? {}),
        ...(accountProfile ?? {}),
    };

    const hasAuthoritativeFetchInputs = Boolean(resolvedAccountId && getAccessTokenSilently);

    const emptyStatus = hasAuthoritativeFetchInputs
        ? computeWildcatterStatus()
        : computeSubscriptionStatus({
            subscriptionRows: [],
            accountProfile: mergedAccountProfile,
            legacySubscriptionStatusId,
            legacyPlanId,
            preferLegacyStripeOnly,
            subscriptionRowsAuthoritative: false,
        });

    if (!hasAuthoritativeFetchInputs) {
        return {
            resolvedAccountId,
            authoritativeAccountProfile: mergedAccountProfile,
            subscriptionRows: [],
            cancelableSubscriptionRows: [],
            cancelableSubscriptionsLoadError: "",
            subscriptionsLoading: false,
            subscriptionsLoadFailed: false,
            subscriptionsError: "",
            subscriptionStatus: emptyStatus,
        };
    }

    try {
        const requests = [
            apiGet(`a_payments/get-subscriptions-new?accountId=${resolvedAccountId}`, getAccessTokenSilently),
        ];

        if (includeCancelable) {
            requests.push(apiGet(`a_payments/get-cancelable-subscriptions-new?accountId=${resolvedAccountId}`, getAccessTokenSilently));
        }

        const [subscriptionsRes, cancelableRes = null] = await Promise.all(requests);

        if (!subscriptionsRes?.ok) {
            const body = await readResponseBody(subscriptionsRes);
            throw new Error(body.json?.message || body.text || "Subscription lookup failed");
        }

        const subscriptionsBody = await readResponseBody(subscriptionsRes);
        const cancelableBody = cancelableRes ? await readResponseBody(cancelableRes) : { json: [], text: "" };
        const subscriptionRows = Array.isArray(subscriptionsBody.json) ? subscriptionsBody.json : [];
        const cancelableSubscriptionRows = includeCancelable && cancelableRes?.ok && Array.isArray(cancelableBody.json) ? cancelableBody.json : [];
        const cancelableSubscriptionsLoadError = includeCancelable && cancelableRes && !cancelableRes.ok
            ? (cancelableBody.json?.message || cancelableBody.text || `Request failed (${cancelableRes.status})`)
            : "";
        const fetchedAccountRecord = await fetchAuthoritativeAccountRecord({ accountId: resolvedAccountId, getAccessTokenSilently });
        const authoritativeAccountProfile = fetchedAccountRecord
            ? { ...mergedAccountProfile, ...fetchedAccountRecord }
            : mergedAccountProfile;
        const subscriptionStatus = computeSubscriptionStatus({
            subscriptionRows,
            accountProfile: authoritativeAccountProfile,
            legacySubscriptionStatusId,
            legacyPlanId,
            preferLegacyStripeOnly,
            subscriptionRowsAuthoritative: true,
        });

        return {
            resolvedAccountId,
            authoritativeAccountProfile,
            subscriptionRows,
            cancelableSubscriptionRows,
            cancelableSubscriptionsLoadError,
            subscriptionsLoading: false,
            subscriptionsLoadFailed: false,
            subscriptionsError: "",
            subscriptionStatus,
        };
    } catch (error) {
        return {
            resolvedAccountId,
            authoritativeAccountProfile: mergedAccountProfile,
            subscriptionRows: [],
            cancelableSubscriptionRows: [],
            cancelableSubscriptionsLoadError: includeCancelable ? "Subscription status load failed" : "",
            subscriptionsLoading: false,
            subscriptionsLoadFailed: true,
            subscriptionsError: error?.message || "Subscription status load failed",
            subscriptionStatus: emptyStatus,
        };
    }
}

export function useMembershipSubscriptionStatus({
    accountId = 0,
    currentAccount = null,
    accountProfile = null,
    getAccessTokenSilently = null,
    includeCancelable = false,
    legacySubscriptionStatusId = 0,
    legacyPlanId = "",
    preferLegacyStripeOnly = false,
    baseUrl = "",
} = {}) {
    const resolvedAccountId = resolveNumericAccountId(accountId, currentAccount, accountProfile);
    const mergedAccountProfile = useMemo(() => ({
        ...(currentAccount ?? {}),
        ...(accountProfile ?? {}),
    }), [currentAccount, accountProfile]);

    const hasAuthoritativeFetchInputs = Boolean(resolvedAccountId && getAccessTokenSilently);

    const [subscriptionRows, setSubscriptionRows] = useState([]);
    const [authoritativeAccountProfile, setAuthoritativeAccountProfile] = useState(mergedAccountProfile);
    const [cancelableSubscriptionRows, setCancelableSubscriptionRows] = useState([]);
    const [cancelableSubscriptionsLoadError, setCancelableSubscriptionsLoadError] = useState("");
    const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
    const [subscriptionsLoadFailed, setSubscriptionsLoadFailed] = useState(false);
    const [subscriptionsError, setSubscriptionsError] = useState("");
    const [reloadNonce, setReloadNonce] = useState(0);
    const [subscriptionStatusReady, setSubscriptionStatusReady] = useState(!hasAuthoritativeFetchInputs);

    const reloadSubscriptions = useCallback(() => {
        setReloadNonce((previousValue) => previousValue + 1);
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const load = async () => {
            if (!resolvedAccountId || !getAccessTokenSilently) {
                if (isCancelled) return;
                setSubscriptionRows([]);
                setAuthoritativeAccountProfile(mergedAccountProfile);
                setCancelableSubscriptionRows([]);
                setCancelableSubscriptionsLoadError("");
                setSubscriptionsLoading(false);
                setSubscriptionsLoadFailed(false);
                setSubscriptionsError("");
                setSubscriptionStatusReady(true);
                return;
            }

            setSubscriptionStatusReady(false);
            setSubscriptionsLoading(true);
            setSubscriptionsLoadFailed(false);
            setSubscriptionsError("");

            const result = await fetchMembershipSubscriptionState({
                accountId: resolvedAccountId,
                currentAccount,
                accountProfile,
                getAccessTokenSilently,
                includeCancelable,
                legacySubscriptionStatusId,
                legacyPlanId,
                preferLegacyStripeOnly,
                baseUrl,
            });

            if (isCancelled) return;

            setSubscriptionRows(Array.isArray(result.subscriptionRows) ? result.subscriptionRows : []);
            setAuthoritativeAccountProfile(result.authoritativeAccountProfile ?? mergedAccountProfile);
            setCancelableSubscriptionRows(Array.isArray(result.cancelableSubscriptionRows) ? result.cancelableSubscriptionRows : []);
            setCancelableSubscriptionsLoadError(result.cancelableSubscriptionsLoadError || "");
            setSubscriptionsLoading(false);
            setSubscriptionsLoadFailed(Boolean(result.subscriptionsLoadFailed));
            setSubscriptionsError(result.subscriptionsError || "");
            setSubscriptionStatusReady(true);
        };

        load();

        return () => {
            isCancelled = true;
        };
    }, [
        resolvedAccountId,
        currentAccount,
        accountProfile,
        getAccessTokenSilently,
        includeCancelable,
        legacySubscriptionStatusId,
        legacyPlanId,
        preferLegacyStripeOnly,
        reloadNonce,
        mergedAccountProfile,
    ]);

    const subscriptionStatus = useMemo(() => {
        return computeSubscriptionStatus({
            subscriptionRows,
            accountProfile: authoritativeAccountProfile ?? mergedAccountProfile,
            legacySubscriptionStatusId,
            legacyPlanId,
            preferLegacyStripeOnly,
            subscriptionRowsAuthoritative: hasAuthoritativeFetchInputs,
        });
    }, [
        subscriptionRows,
        authoritativeAccountProfile,
        mergedAccountProfile,
        legacySubscriptionStatusId,
        legacyPlanId,
        preferLegacyStripeOnly,
        hasAuthoritativeFetchInputs,
    ]);

    return {
        resolvedAccountId,
        subscriptionRows,
        cancelableSubscriptionRows,
        cancelableSubscriptionsLoadError,
        subscriptionsLoading,
        subscriptionsLoadFailed,
        subscriptionsError,
        subscriptionStatusReady,
        subscriptionStatus,
        effectivePlanId: subscriptionStatus?.effectivePlanId ?? PLAN_ID.wildcatter,
        reloadSubscriptions,
    };
} 